// Общий блок «Пробные уроки» (бывш. «Мои пробные» из CallerDashboard).
// Вынесен отдельным файлом, чтобы не плодить копии: любые правки кнопок/модалок
// сразу действуют во всех кабинетах, где блок подключён (Антон 07.08).
import { useState, useEffect, useCallback } from 'react'
import api from '../api'
import { SmsLinkRow } from './LessonBookingBlock'
import TrialSignupModal, { formatTeacherPhone, telHref } from './TrialSignupModal'
import { TutorPickerCard, TutorWeekSlots, TutorPickerSubjectHint } from './TutorPicker'

// ─────────── Мои пробные ───────────
const TRIAL_STATUS_META = {
  new:       { label: 'Предстоит', bg: '#F0F9FF', fg: '#0369A1', ring: '#BAE6FD', dot: '#38BDF8' },
  planned:   { label: 'Предстоит', bg: '#F0F9FF', fg: '#0369A1', ring: '#BAE6FD', dot: '#38BDF8' },
  confirmed: { label: 'Предстоит', bg: '#F0F9FF', fg: '#0369A1', ring: '#BAE6FD', dot: '#38BDF8' },
  done:      { label: 'Пришёл',    bg: '#ECFDF5', fg: '#047857', ring: '#A7F3D0', dot: '#10B981' },
  noshow:    { label: 'Не пришёл', bg: '#FEF2F2', fg: '#B91C1C', ring: '#FECACA', dot: '#EF4444' },
}
export function formatTrialDate(iso) {
  if (!iso) return ''
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) }
  catch { return iso }
}
// «Встал ли клиент в постоянное расписание после пробного» (Антон 22.07) — только
// для проведённых пробных (it.stayed === null для остальных статусов, см. бэк
// платформы /api/internal/trials). Три состояния:
//  1. nextLessonDate есть — встал, показываем конкретную дату/время.
//  2. stayed=true, но nextLessonDate нет — уроки были, но следующего пока не назначено.
//  3. stayed=false — на постоянные так и не записался.
function ScheduleStatusChip({ item }) {
  if (item.stayed === null || item.stayed === undefined) return null
  const base = 'inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-full mt-1.5 w-fit'
  if (item.nextLessonDate) {
    return (
      <div className={base} style={{ background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0' }}>
        Встал в расписание — {formatTrialDate(item.nextLessonDate)}, {item.nextLessonTime}
      </div>
    )
  }
  if (item.stayed) {
    return (
      <div className={base} style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }}>
        Занимался, сейчас без урока в расписании
      </div>
    )
  }
  return (
    <div className={base} style={{ background: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A' }}>
      В расписание не встал
    </div>
  )
}
// Плашка «подтвердил ли препод» убрана 17.09 (Антон: «убери все упоминания о том, что
// подтвердил препод или нет») — с 08.09 самозапись, с 17.09 запись менеджером подтверждения
// не требуют; teacherOfferStatus с бэка больше не читаем на карточке.

function TrialSignalChip({ on, children }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
      style={on
        ? { background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0' }
        : { background: 'transparent', color: '#98A2B3', border: '1px solid #E4E7EC' }}>
      {children}
    </span>
  )
}
// Кнопка «Изменить время» доступна пока урок ещё впереди/только что прошёл мимо
// (в т.ч. noshow — клиент не пришёл, но перезвонил и хочет пересобраться) и на нём
// уже стоит реальный препод (не «идёт подбор замены» — там неясно чьё расписание
// смотреть). Антон 2026-07-20: реальный случай — Сидоров noshow, перенести не могли.
function canReschedule(it) {
  return (it.status === 'new' || it.status === 'planned' || it.status === 'confirmed' || it.status === 'noshow') && !!it.assignedTeacher
}

function RescheduleModal({ item, onClose, onDone }) {
  const [slots, setSlots] = useState(null)
  const [error, setError] = useState(null)
  const [activeDate, setActiveDate] = useState(null)
  const [chosen, setChosen] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let alive = true
    api.get(`/api/platform/my-trials/${item.id}/reschedule-slots`)
      .then(r => {
        if (!alive) return
        if (!r.data.ok) { setError(r.data.error || 'Не удалось получить окошки'); return }
        const s = r.data.slots || []
        setSlots(s)
        if (s.length) setActiveDate(s[0].date)
      })
      .catch(e => { if (alive) setError(e.response?.data?.error || 'Платформа недоступна') })
    return () => { alive = false }
  }, [item.id])

  const days = slots ? [...new Set(slots.map(s => s.date))] : []
  const daySlots = slots ? slots.filter(s => s.date === activeDate) : []

  async function confirm() {
    if (!chosen) return
    setSaving(true)
    setError(null)
    try {
      const r = await api.post(`/api/platform/my-trials/${item.id}/reschedule`, {
        new_date: chosen.date, new_begin_time: chosen.begin_time,
        // для журнала правок: что было и чей это клиент (информационно)
        old_date: item.date, old_time: item.time,
        client_name: item.clientName, subject: item.subject,
      })
      if (!r.data.ok) { setError(r.data.error || 'Не удалось перенести'); setSaving(false); return }
      onDone()
    } catch (e) {
      setError(e.response?.data?.error || 'Платформа недоступна')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="text-[15px] font-bold text-slate-900 mb-1">Перенести пробный</div>
        <div className="text-[12.5px] text-slate-500 mb-4">
          {item.clientName} · сейчас {formatTrialDate(item.date)}, {item.time} · препод {item.assignedTeacher}
        </div>

        {error && <div className="text-[13px] text-red-600 mb-3">{error}</div>}
        {!slots && !error && <div className="text-[13px] text-slate-400 py-6 text-center">Загрузка окошек...</div>}
        {slots && slots.length === 0 && <div className="text-[13px] text-slate-400 py-6 text-center">У препода нет свободных окошек</div>}

        {days.length > 0 && (
          <>
            <div className="flex gap-1.5 flex-wrap mb-3">
              {days.map(d => (
                <button key={d} onClick={() => { setActiveDate(d); setChosen(null) }}
                  className="text-[12px] font-semibold px-3 py-1.5 rounded-lg"
                  style={activeDate === d ? { background: '#F0F9FF', color: '#0369A1' } : { background: '#F4F6F8', color: '#667085' }}>
                  {formatTrialDate(d)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {daySlots.map(s => (
                <button key={s.begin_time} onClick={() => setChosen(s)}
                  className="text-[13px] font-semibold py-2 rounded-lg"
                  style={chosen === s ? { background: '#38BDF8', color: '#fff' } : { background: '#F4F6F8', color: '#667085' }}>
                  {s.begin_time}
                </button>
              ))}
            </div>
          </>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-[13px] font-semibold px-4 py-2 rounded-lg text-slate-500">Отмена</button>
          <button onClick={confirm} disabled={!chosen || saving}
            className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-40"
            style={{ background: '#38BDF8' }}>
            {saving ? 'Переношу...' : chosen ? `Подтвердить на ${chosen.begin_time}` : 'Выберите время'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Что стало с преподавателем на пробном. Раньше звонильщица видела ГОЛОЕ имя и не
// понимала, тот это препод, которого она поставила, или его автоматически заменили
// (Антон 03.08: «при автозамене звонильщик должен видеть, на кого заменился урок»).
// Те же состояния, что уже показываются сопровождению в SupportDashboard.
function ReplacementNote({ item }) {
  const st = item.replacementStatus
  if (!st || st === 'direct') return null

  if (st === 'assigned') {
    return (
      <span className="inline-block text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full ml-1.5 align-middle"
            style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A' }}>
        замена
      </span>
    )
  }
  const text = st === 'searching'
    ? (item.waitingCandidate ? 'идёт подбор, ждём ответа: ' + item.waitingCandidate : 'идёт подбор преподавателя')
    : st === 'not_found' ? 'преподаватель не найден'
    : 'подбор ещё не запускался'
  return <div className="text-[11.5px] text-amber-700 mt-0.5">{text}</div>
}

function ChangeTeacherModal({ item, onClose, onDone }) {
  const [candidates, setCandidates] = useState(null)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState('')
  const [chosen, setChosen] = useState(null)
  const [slot, setSlot] = useState(null)   // выбранное окошко {date, time}; null = оставить текущее время
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let alive = true
    // Кандидаты (нужный предмет, свободен/занят в это время) + богатые данные из
    // каталога (цена, конверсия, фото, возраст, отзыв сопроводителя) — мержим по mkId.
    Promise.all([
      api.get(`/api/platform/my-trials/${item.id}/teacher-candidates`).then(r => r.data).catch(e => ({ error: e.response?.data?.error || 'Платформа недоступна' })),
      api.get('/api/platform/tutors', { params: { subject: item.subject } }).then(r => r.data).catch(() => ({ tutors: [] })),
    ]).then(([cand, tut]) => {
      if (!alive) return
      if (cand.error || !cand.ok) { setError(cand.error || 'Не удалось получить список преподов'); return }
      const rich = {}
      ;(tut.tutors || []).forEach(t => { rich[t.mkId] = t })
      setCandidates((cand.candidates || []).map(c => ({ ...(rich[c.mkId] || {}), ...c })))
    })
    return () => { alive = false }
  }, [item.id, item.subject])

  const lt = String(item.time || '').slice(0, 5)
  const filtered = (candidates || [])
    .filter(c => c.name.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) =>
      ((b.otbor && b.otbor.waiting ? 1 : 0) - (a.otbor && a.otbor.waiting ? 1 : 0)) ||
      ((b.free ? 1 : 0) - (a.free ? 1 : 0)) ||
      // приоритет СРМ (платформа teacher_rank, 05.09): дно — вниз, выше приоритет — выше
      (((a.rank && a.rank.crm_floor) ? 1 : 0) - ((b.rank && b.rank.crm_floor) ? 1 : 0)) ||
      (((b.rank && b.rank.crm_rank) || 0) - ((a.rank && a.rank.crm_rank) || 0)) ||
      ((b.conv || 0) - (a.conv || 0)) ||
      a.name.localeCompare(b.name, 'ru'))

  const target = slot || { date: item.date, time: lt }
  const timeChanged = target.date !== item.date || target.time !== lt

  async function confirm() {
    if (!chosen) return
    setSaving(true)
    setError(null)
    try {
      const r = await api.post(`/api/platform/my-trials/${item.id}/change-teacher`, {
        teacher_mk_id: chosen.mkId,
        old_teacher: item.assignedTeacher, client_name: item.clientName, subject: item.subject,
      })
      if (!r.data.ok) { setError(r.data.error || 'Не удалось сменить препода'); setSaving(false); return }
      // Выбрали другое окошко — переносим урок на новое время (тем же эндпоинтом, что «Перенести»).
      if (timeChanged) {
        const r2 = await api.post(`/api/platform/my-trials/${item.id}/reschedule`, {
          new_date: target.date, new_begin_time: target.time,
          old_date: item.date, old_time: item.time,
          client_name: item.clientName, subject: item.subject,
        })
        if (!r2.data.ok) { setError('Препод сменён, но перенос не удался: ' + (r2.data.error || '')); setSaving(false); return }
      }
      onDone()
    } catch (e) {
      setError(e.response?.data?.error || 'Платформа недоступна')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="text-[15px] font-bold text-slate-900 mb-1">Сменить преподавателя</div>
        <div className="text-[12.5px] text-slate-500 mb-4">
          {item.clientName}{item.subject ? <> · <b className="text-slate-700">{item.subject}{item.grade ? `, ${item.grade} класс` : ''}</b></> : null} · {formatTrialDate(item.date)}, {item.time} · сейчас {item.assignedTeacher}
        </div>

        {error && <div className="text-[13px] text-red-600 mb-3">{error}</div>}
        {!candidates && !error && <div className="text-[13px] text-slate-400 py-6 text-center">Загрузка списка...</div>}

        {candidates && (
          <div className="grid gap-4 min-h-0 flex-1" style={{ gridTemplateColumns: chosen ? '1.1fr 1fr' : '1fr' }}>
            <div className="min-h-0 flex flex-col">
              <TutorPickerSubjectHint subject={item.subject} grade={item.grade} />
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Поиск по имени..."
                className="w-full text-[13px] px-3 py-2 rounded-lg border border-slate-200 mb-3 outline-none focus:border-sky-300" />
              <div className="overflow-y-auto flex flex-col gap-2 pr-1">
                {filtered.length === 0 && <div className="text-[13px] text-slate-400 py-4 text-center">Никого не нашлось</div>}
                {filtered.map(c => (
                  <TutorPickerCard key={c.mkId} t={c} selected={chosen?.mkId === c.mkId}
                    atSlotFree={c.free} onClick={() => { setChosen(c); setSlot(null) }} />
                ))}
              </div>
            </div>
            {chosen && (
              <div className="min-h-0 overflow-y-auto">
                <TutorWeekSlots mkId={chosen.mkId} lessonDate={item.date} lessonTime={item.time}
                  value={slot} onChange={setSlot} />
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 mt-4">
          <div className="text-[11.5px] text-slate-500">
            {chosen
              ? <>Выбран(а) <b className="text-slate-700">{chosen.name.split(' ').slice(0, 2).join(' ')}</b>{chosen.price != null ? ` · ${chosen.price} ₽` : ''}{timeChanged ? ` · перенос на ${formatTrialDate(target.date)}, ${target.time}` : ''}</>
              : 'Выберите преподавателя'}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-[13px] font-semibold px-4 py-2 rounded-lg text-slate-500">Отмена</button>
            <button onClick={confirm} disabled={!chosen || saving}
              className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-40"
              style={{ background: '#38BDF8' }}>
              {saving ? 'Сохраняю...' : chosen ? `Назначить ${chosen.name.split(' ')[0]}` : 'Выберите препода'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Журнал правок (Антон 07.08: страница общая, каждая правка пишется навсегда) ───
const LOG_ACTION_LABEL = {
  book: 'Записал(а) пробный',
  reschedule: 'Перенёс(ла) пробный',
  change_teacher: 'Сменил(а) препода',
  change_manager: 'Передал(а) ответственного',
}
const LOG_ACTION_SHORT = { reschedule: 'Перенос', change_teacher: 'Смена препода', change_manager: 'Смена ответственного' }

// «2026-08-07 10:12:33» (localtime БД) → «7 августа, 10:12»
function fmtLogAt(at) {
  if (!at) return ''
  const [d, t] = at.split(' ')
  return formatTrialDate(d) + (t ? ', ' + t.slice(0, 5) : '')
}

// Серая пометка на строке пробного: кто последним его трогал
function LastEditNote({ edit }) {
  if (!edit) return null
  return (
    <div className="text-[11px] text-slate-400 mt-1">
      {(LOG_ACTION_SHORT[edit.action] || 'Правка')} — {(edit.user || '').split(' ')[0]} · {fmtLogAt(edit.at)}
    </div>
  )
}

function TrialsLogModal({ onClose }) {
  const [items, setItems] = useState(null)
  const [error, setError] = useState(null)
  const [q, setQ] = useState('')

  useEffect(() => {
    let alive = true
    api.get('/api/platform/my-trials-log')
      .then(r => { if (alive) setItems(r.data.items || []) })
      .catch(e => { if (alive) setError(e.response?.data?.error || 'Не удалось загрузить журнал') })
    return () => { alive = false }
  }, [])

  const ql = q.trim().toLowerCase()
  const filtered = (items || []).filter(r =>
    !ql || (r.client_name || '').toLowerCase().includes(ql) || (r.user_name || '').toLowerCase().includes(ql))

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 pt-16" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-3xl w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center mb-1">
          <div className="text-[15px] font-bold text-slate-900">Журнал правок</div>
          <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-600 text-[18px] leading-none">✕</button>
        </div>
        <div className="text-[12.5px] text-slate-400 mb-3">
          Все изменения пробных всеми менеджерами. Удалить запись из журнала нельзя.
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Поиск по клиенту или менеджеру"
          className="w-full text-[13px] px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 mb-3 outline-none focus:border-sky-300 focus:bg-white" />
        {error && <div className="text-[13px] text-red-600 py-4">{error}</div>}
        {!items && !error && <div className="text-[13px] text-slate-400 py-6 text-center">Загрузка...</div>}
        {items && filtered.length === 0 && (
          <div className="text-[13px] text-slate-400 py-6 text-center">{ql ? 'Ничего не нашлось' : 'Правок пока не было'}</div>
        )}
        {filtered.length > 0 && (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-semibold py-1.5 pr-3">Когда</th>
                  <th className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-semibold py-1.5 pr-3">Кто</th>
                  <th className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-semibold py-1.5 pr-3">Что сделал</th>
                  <th className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-semibold py-1.5">Клиент</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100 text-[13px] align-top">
                    <td className="py-2.5 pr-3 whitespace-nowrap text-slate-500">{fmtLogAt(r.at)}</td>
                    <td className="py-2.5 pr-3 whitespace-nowrap font-semibold text-slate-700">{(r.user_name || '').split(' ')[0]}</td>
                    <td className="py-2.5 pr-3 text-slate-700">
                      {LOG_ACTION_LABEL[r.action] || r.action}{r.details ? ': ' + r.details : ''}
                    </td>
                    <td className="py-2.5 text-slate-500">{r.client_name || '—'}{r.subject ? ' · ' + r.subject : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────── Дни, фильтры и подсказки списка (Антон 02.10) ───────────
const MSK_MS = 3 * 3600000
const DAY_ORDER = ['today', 'tomorrow', 'later', 'past']
const DAY_TITLE = { today: 'Сегодня', tomorrow: 'Завтра', later: 'Позже', past: 'Прошедшие' }
const isoMsk = (ms) => new Date(ms + MSK_MS).toISOString().slice(0, 10)
function trialDayKey(it, todayIso, tomorrowIso) {
  if (it.date < todayIso) return 'past'
  if (it.date === todayIso) return 'today'
  if (it.date === tomorrowIso) return 'tomorrow'
  return 'later'
}
// Что подсказать звонильщице по одному пробному: «через N», нужно ли действие, когда придёт напоминание.
function trialInfo(it, now, todayIso) {
  const upcoming = it.status === 'new' || it.status === 'planned' || it.status === 'confirmed'
  const out = { needs: false, left: null, remind: null }
  if (!upcoming || !it.date || !it.time) return out
  const startMs = new Date(it.date + 'T' + it.time + ':00+03:00').getTime()
  if (!(startMs > now)) return out
  const diff = Math.round((startMs - now) / 60000)
  if (diff < 60) out.left = 'через ' + diff + ' мин'
  else if (diff < 24 * 60) out.left = 'через ' + Math.floor(diff / 60) + ' ч' + (diff % 60 ? ' ' + (diff % 60) + ' мин' : '')
  else {
    const days = Math.round((Date.parse(it.date) - Date.parse(todayIso)) / 86400000)
    out.left = days === 1 ? 'завтра' : 'через ' + days + ' дн'
  }
  // Напоминание клиенту уходит за час до урока. Ушло, а клиент не нажал «приду» — нужен звонок.
  if (it.notified && !it.clientConfirmed) out.needs = true
  if (!it.notified) {
    const remindMs = startMs - 3600000
    if (remindMs > now) {
      out.remind = 'Напоминание клиенту придёт ' + (it.date === todayIso ? '' : formatTrialDate(it.date) + ' ') + 'в ' + new Date(remindMs + MSK_MS).toISOString().slice(11, 16)
    }
  }
  return out
}

export default function TrialsBoard() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  // «Все / Только мои»: по умолчанию свои, «Все» — чтобы подхватить пробные коллеги
  const [scope, setScope] = useState('mine')
  const [logOpen, setLogOpen] = useState(false)
  const [dayFilter, setDayFilter] = useState('all')
  const [rescheduleItem, setRescheduleItem] = useState(null)
  const [teacherChangeItem, setTeacherChangeItem] = useState(null)
  // «Ещё пробный»: клиент уже записан на один предмет и просит второй. В очереди его
  // больше нет (статус лида = trial, см. routes/deals.js), поэтому единственная точка
  // возврата к нему — этот список.
  const [extraTrialItem, setExtraTrialItem] = useState(null)
  const [extraBooked, setExtraBooked] = useState([])
  // «Сменить ответственного» (Антон 12.08): к новому менеджеру переходит ВСЁ —
  // пробный в плане/премии/KPI и клиент с задачами. Список звонильщиц берём из
  // готового рейтинга (/api/managers-ranking), отдельного справочника не плодим.
  const [mgrMenuFor, setMgrMenuFor] = useState(null)   // booking id с открытым списком
  const [mgrNames, setMgrNames] = useState(null)
  const [mgrSaving, setMgrSaving] = useState(false)

  const openMgrMenu = (id) => {
    setMgrMenuFor(prev => (prev === id ? null : id))
    if (!mgrNames) {
      api.get('/api/managers-ranking')
        .then(r => setMgrNames(((r.data.calls && r.data.calls.today) || []).map(m => m.name)))
        .catch(() => setMgrNames([]))
    }
  }

  const handleChangeManager = async (it, managerName) => {
    setMgrSaving(true)
    try {
      await api.post(`/api/platform/my-trials/${it.id}/change-manager`, { managerName })
      setMgrMenuFor(null)
      load()
    } catch (e) {
      alert('Не удалось передать: ' + ((e.response && e.response.data && e.response.data.error) || e.message))
    }
    setMgrSaving(false)
  }

  const load = useCallback(() => {
    setLoading(true)
    return api.get('/api/platform/my-trials?days=30&scope=' + scope)
      .then(r => setData(r.data))
      .catch(() => setData({ ok: false, items: [], summary: null }))
      .finally(() => setLoading(false))
  }, [scope])

  useEffect(() => { load() }, [load])

  // Тот же вызов, что и из карточки сделки: бронь платформы на тот же leadId, но с
  // другим предметом. Бэкенд повторную бронь по лиду разрешает, а lead_events пишет
  // отдельным событием — премия за него считается как за самостоятельный пробный.
  const handleExtraTrial = async (params) => {
    const it = extraTrialItem
    if (!it) return
    try {
      const { data: res } = await api.post('/api/platform/book', {
        leadId:      it.leadId,
        teacherMkId: params.teacherMkId,
        teacherName: params.teacherName,
        subject:     params.subject,
        date:        params.date,
        time:        params.time,
        comment:     params.comment || '',
        childPhone:  it.childPhone || '',
        childId:     it.childId || null,
      })
      setExtraTrialItem(null)
      setExtraBooked(prev => [...prev, {
        clientName: it.clientName, subject: params.subject, date: params.date,
        time: params.time, teacherName: params.teacherName,
        // id брони — для кнопки «Отправить SMS со ссылкой» (09.09.2026)
        bookingId: (res.booking && res.booking.id) || null,
        phone: it.phone || it.childPhone || null,
      }])
      load()
    } catch (e) {
      const body = (e.response && e.response.data) || {}
      alert('Не удалось записать:\n' + (body.error || e.message || 'unknown'))
    }
  }

  if (loading) return (
    <div className="rounded-2xl p-12 bg-white text-center text-slate-400 text-[15px]" style={{ border: '2px solid #38BDF8' }}>
      Загрузка...
    </div>
  )

  const items = (data && data.items) || []
  const summary = (data && data.summary) || { total: 0, upcoming: 0, done: 0, noshow: 0 }

  // Дни по московскому времени; Сегодня/Завтра/Позже — по возрастанию, Прошедшие — свежие сверху.
  const nowMs = Date.now()
  const todayIso = isoMsk(nowMs)
  const tomorrowIso = isoMsk(nowMs + 86400000)
  const decorated = items.map(it => ({ it, day: trialDayKey(it, todayIso, tomorrowIso), info: trialInfo(it, nowMs, todayIso) }))
  const dayCounts = { all: decorated.length, needs: decorated.filter(r => r.info.needs).length, today: 0, tomorrow: 0, later: 0, past: 0 }
  decorated.forEach(r => { dayCounts[r.day]++ })
  const rowsView = []
  DAY_ORDER.forEach(d => {
    const group = decorated
      .filter(r => r.day === d && (dayFilter === 'all' || (dayFilter === 'needs' ? r.info.needs : dayFilter === d)))
      .sort((a, b) => {
        const ka = a.it.date + ' ' + a.it.time, kb = b.it.date + ' ' + b.it.time
        if (ka === kb) return 0
        return (d === 'past' ? ka < kb : ka > kb) ? 1 : -1
      })
    if (!group.length) return
    rowsView.push({ header: true, key: d, title: DAY_TITLE[d], sub: (d === 'today' || d === 'tomorrow') ? formatTrialDate(d === 'today' ? todayIso : tomorrowIso) : '' })
    group.forEach(r => rowsView.push(r))
  })

  return (
    <div className="rounded-2xl bg-white overflow-hidden" style={{ border: '2px solid #38BDF8' }}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className="text-[16px] font-semibold text-slate-900">Пробные уроки</div>
        <div className="ml-auto flex rounded-lg overflow-hidden border border-slate-200 text-[12px] font-bold">
          <button onClick={() => setScope('all')}
            className="px-3.5 py-1.5"
            style={scope === 'all' ? { background: '#0284C7', color: '#fff' } : { background: '#fff', color: '#64748B' }}>
            Все
          </button>
          <button onClick={() => setScope('mine')}
            className="px-3.5 py-1.5"
            style={scope === 'mine' ? { background: '#0284C7', color: '#fff' } : { background: '#fff', color: '#64748B' }}>
            Только мои
          </button>
        </div>
        <button onClick={() => setLogOpen(true)}
          className="text-[12px] font-bold px-3.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-sky-300 hover:text-sky-800">
          Журнал правок
        </button>
        <div className="text-[12px] text-slate-400">за последние 30 дней</div>
      </div>

      <div className="grid grid-cols-4 divide-x divide-slate-100 border-b border-slate-100">
        <div className="px-5 py-3.5">
          <div className="text-[20px] font-bold text-slate-900">{summary.total}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">всего поставлено</div>
        </div>
        <div className="px-5 py-3.5">
          <div className="text-[20px] font-bold" style={{ color: '#0369A1' }}>{summary.upcoming}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">ещё впереди</div>
        </div>
        <div className="px-5 py-3.5">
          <div className="text-[20px] font-bold" style={{ color: '#047857' }}>{summary.done}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">прошли</div>
        </div>
        <div className="px-5 py-3.5">
          <div className="text-[20px] font-bold" style={{ color: '#B91C1C' }}>{summary.noshow}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">не пришли</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 px-5 py-3 border-b border-slate-100">
        {[['all', 'Все'], ['needs', 'Нужно действие'], ['today', 'Сегодня'], ['tomorrow', 'Завтра'], ['later', 'Позже'], ['past', 'Прошедшие']].map(([k, label]) => (
          <button key={k} onClick={() => setDayFilter(k)}
            className="text-[13px] font-semibold px-3 py-1.5 rounded-full"
            style={dayFilter === k
              ? { background: '#F0F9FF', color: '#0369A1', border: '1px solid #BAE6FD' }
              : { background: '#fff', color: '#475569', border: '1px solid #E2E8F0' }}>
            {label} <b style={{ color: k === 'needs' && dayCounts.needs ? '#B45309' : undefined }}>{dayCounts[k]}</b>
          </button>
        ))}
      </div>

      {extraBooked.map((b, i) => (
        <div key={i} className="mx-5 mt-3 rounded-xl px-4 py-3 text-[13px]"
          style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', color: '#047857' }}>
          <span className="font-semibold">Записан — {b.clientName} · {b.subject}</span>
          <span className="text-[12px] ml-2" style={{ color: '#065F46' }}>
            {formatTrialDate(b.date)}, {b.time} · {b.teacherName}
          </span>
          <div style={{ marginLeft: -32 }}><SmsLinkRow bookingId={b.bookingId} phone={b.phone} /></div>
        </div>
      ))}

      {!items.length ? (
        <div className="p-12 text-center text-slate-400 text-[14px]">Пока нет ни одного пробного</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <colgroup>
              <col style={{ width: scope === 'all' ? '26%' : '30%' }} />
              {scope === 'all' && <col style={{ width: '13%' }} />}
              <col style={{ width: scope === 'all' ? '25%' : '28%' }} />
              <col style={{ width: scope === 'all' ? '14%' : '16%' }} />
              <col style={{ width: scope === 'all' ? '22%' : '26%' }} />
            </colgroup>
            <thead>
              <tr>
                <th className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-5 pt-3 pb-2">Клиент</th>
                {scope === 'all' && (
                  <th className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-5 pt-3 pb-2">Записал</th>
                )}
                <th className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-5 pt-3 pb-2">Преподаватель</th>
                <th className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-5 pt-3 pb-2">Когда</th>
                <th className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-5 pt-3 pb-2">Статус</th>
              </tr>
            </thead>
            <tbody>
              {!rowsView.length && (
                <tr><td colSpan={scope === 'all' ? 5 : 4} className="px-5 py-8 text-center text-[13px] text-slate-400">В этом разделе пока ничего нет</td></tr>
              )}
              {rowsView.map(row => {
                if (row.header) return (
                  <tr key={'day-' + row.key}>
                    <td colSpan={scope === 'all' ? 5 : 4} className="px-5 py-2 text-[12px] font-bold uppercase tracking-wider text-slate-500" style={{ background: '#F1F5F9' }}>
                      {row.title}{row.sub ? <span className="ml-2 font-normal normal-case tracking-normal text-slate-400">{row.sub}</span> : null}
                    </td>
                  </tr>
                )
                const it = row.it
                const info = row.info
                const meta = TRIAL_STATUS_META[it.status] || TRIAL_STATUS_META.planned
                return (
                  <tr key={it.id} className="border-t border-slate-100" style={info.needs ? { background: '#FFFBEB' } : undefined}>
                    <td className="px-5 py-3 align-top">
                      <div className="text-[13.5px] font-semibold text-slate-900">{it.clientName}</div>
                      {it.parentName && (
                        <div className="text-[12px] mt-0.5" style={{ color: '#334155' }}>
                          <span style={{ fontWeight: 600 }}>Родитель:</span> {it.parentName}
                        </div>
                      )}
                      <div className="text-[12px] text-slate-500 mt-0.5">{it.subject}{it.grade ? ' · ' + it.grade + ' класс' : ''}</div>
                      {it.clientPhone && <div className="text-[12px] text-slate-400 mt-0.5">{it.clientPhone}</div>}
                      {it.childPhone && <div className="text-[12px] text-slate-400 mt-0.5">📱 ребёнок: {it.childPhone}</div>}
                      {info.needs && (
                        <div className="text-[12px] font-semibold mt-1.5 px-2 py-1 rounded-lg"
                          style={{ background: '#fff', color: '#B45309', border: '1px solid #FDE68A' }}>
                          Клиент не подтвердил урок. Позвоните и уточните, придёт ли.
                        </div>
                      )}
                      <div>
                        <button onClick={() => setExtraTrialItem(it)}
                          className="text-[11.5px] font-semibold px-2 py-1 rounded-lg whitespace-nowrap mt-1.5"
                          style={{ background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0' }}>
                          ➕ Ещё пробный
                        </button>
                      </div>
                    </td>
                    {scope === 'all' && (
                      <td className="px-5 py-3 align-top">
                        <div className="text-[12px] font-semibold"
                          style={{ color: it.mine ? '#0369A1' : '#64748B' }}>
                          {(it.bookedBy || '—').split(' ')[0]}
                        </div>
                        {it.mine && <div className="text-[10.5px] text-slate-400 mt-0.5">это я</div>}
                        <div className="relative">
                          {/* Кнопка была nowrap и вылезала из узкой колонки «Записал»,
                              налезая на «Сменить препода» (Роман 23.09.2026) — теперь
                              переносится по словам и не шире своей колонки. */}
                          <button onClick={() => openMgrMenu(it.id)}
                            className="text-[11.5px] font-semibold px-2 py-1 rounded-lg mt-1.5 text-left leading-tight"
                            style={{ background: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE', maxWidth: '100%' }}>
                            Сменить ответственного
                          </button>
                          {mgrMenuFor === it.id && (
                            <div className="absolute z-20 mt-1 bg-white rounded-xl shadow-lg p-1.5"
                              style={{ border: '1px solid #E2E8F0', minWidth: 210 }}>
                              <div className="text-[10.5px] font-bold text-slate-400 px-2 py-1" style={{ letterSpacing: '.06em' }}>ПЕРЕДАТЬ ПРОБНЫЙ</div>
                              {mgrNames === null && <div className="text-[12px] text-slate-400 px-2 py-1.5">Загрузка...</div>}
                              {(mgrNames || []).filter(n => n !== it.bookedBy).map(n => (
                                <button key={n} disabled={mgrSaving}
                                  onClick={() => handleChangeManager(it, n)}
                                  className="block w-full text-left text-[13px] px-2.5 py-1.5 rounded-lg hover:bg-slate-50"
                                  style={mgrSaving ? { opacity: 0.5 } : {}}>
                                  {n}{n === localStorage.getItem('name') ? ' (себе)' : ''}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-5 py-3 align-top text-[13px] text-slate-500">
                      <div>
                        {it.assignedTeacher || '—'}
                        {it.replacementStatus === 'assigned' && <ReplacementNote item={it} />}
                      </div>
                      {it.replacementStatus !== 'assigned' && <ReplacementNote item={it} />}
                      {it.replacementStatus === 'assigned' && it.rejectedNames && it.rejectedNames.length > 0 && (
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          отказались: {it.rejectedNames.join(', ')}
                        </div>
                      )}
                      {formatTeacherPhone(it.teacherPhone) && (
                        <a href={telHref(it.teacherPhone)}
                           className="text-[11.5px] text-slate-400 hover:text-sky-600 block mt-0.5">
                          {formatTeacherPhone(it.teacherPhone)}
                        </a>
                      )}
                      {canReschedule(it) && (
                        <button onClick={() => setTeacherChangeItem(it)}
                          className="text-[11.5px] font-semibold px-2 py-1 rounded-lg whitespace-nowrap mt-1"
                          style={{ background: '#F5F3FF', color: '#6D28D9', border: '1px solid #DDD6FE' }}>
                          👤 Сменить препода
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-3 align-top">
                      <div className="flex flex-wrap items-center gap-2">
                        <div>
                          <div className="text-[13px] font-semibold text-slate-900">{formatTrialDate(it.date)}</div>
                          <div className="text-[12px] text-slate-400">{it.time}</div>
                          {info.left && <div className="text-[11.5px] font-semibold text-slate-400 mt-0.5">{info.left}</div>}
                        </div>
                        {canReschedule(it) && (
                          <button onClick={() => setRescheduleItem(it)}
                            className="text-[11.5px] font-semibold px-2 py-1 rounded-lg whitespace-nowrap"
                            style={{ background: '#F0F9FF', color: '#0369A1', border: '1px solid #BAE6FD' }}>
                            ✏️ Изменить
                          </button>
                        )}
                      </div>
                      <LastEditNote edit={it.lastEdit} />
                    </td>
                    <td className="px-5 py-3 align-top">
                      <span className="inline-flex items-center gap-1.5 text-[11.5px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap"
                        style={{ background: meta.bg, color: meta.fg, border: `1px solid ${meta.ring}` }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
                        {meta.label}
                      </span>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        <TrialSignalChip on={it.loggedIn}>🔓 Вошёл в ЛК</TrialSignalChip>
                        <TrialSignalChip on={!!it.clientConfirmed}>☎️ Подтвердил за 1ч</TrialSignalChip>
                      </div>
                      <ScheduleStatusChip item={it} />
                      {info.remind && <div className="text-[11px] text-slate-400 mt-1.5">{info.remind}</div>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {rescheduleItem && (
        <RescheduleModal
          item={rescheduleItem}
          onClose={() => setRescheduleItem(null)}
          onDone={() => { setRescheduleItem(null); load() }}
        />
      )}

      {teacherChangeItem && (
        <ChangeTeacherModal
          item={teacherChangeItem}
          onClose={() => setTeacherChangeItem(null)}
          onDone={() => { setTeacherChangeItem(null); load() }}
        />
      )}

      {logOpen && <TrialsLogModal onClose={() => setLogOpen(false)} />}

      <TrialSignupModal
        showAllTeachers
        open={!!extraTrialItem}
        onClose={() => setExtraTrialItem(null)}
        clientLabel={extraTrialItem
          ? `${extraTrialItem.clientName} · уже записан на «${extraTrialItem.subject || '—'}»`
          : ''}
        initial={extraTrialItem
          ? { subject: extraTrialItem.subject, classNum: extraTrialItem.grade, comment: '' }
          : {}}
        onSubmit={handleExtraTrial}
      />
    </div>
  )
}
